import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { syncService } from '../../../services/syncService';
import { useSyncStartDateStore } from '../../../stores/syncStartDateStore';
import { Button } from '../../../ui/Button/Button';
import { DatePicker } from '../../../ui/DatePicker/DatePicker';
import { TranslationKey } from '../../../i18n/translationKeys';
import { getEarliestAllowedDate, getTodayDate } from '../../../utils/sync/syncStartDate';
import { SyncStartDateResyncPrompt } from './SyncStartDateResyncPrompt';
import { SyncStartDateFeedback } from './SyncStartDateFeedback';
import { useSync } from '../../../hooks/useSync';
import { useFullSyncOnHome } from '../../../hooks/useFullSyncOnHome';
import type { SyncStartDatePanelStatus, SyncStartDateResyncOutcome } from './types';

/**
 * Lets the user view and change the sync start date at any time (transactions dated before it are not
 * synced / do not generate follow-ups). Lives in Settings so it stays adjustable after the initial sync,
 * not only on the first-sync screen. When the saved date moves the window, the user is offered a full
 * sync right away — that's the only sync that re-reads the source from the new date.
 */
export const SyncStartDatePanel: React.FC = () => {
    const { t } = useTranslation();
    const { syncing, canSync } = useSync();
    const syncOnHome = useFullSyncOnHome();
    const [status, setStatus] = useState<SyncStartDatePanelStatus>('loading');
    const [currentStartDate, setCurrentStartDate] = useState<string | null>(null);
    // The date the backend actually filters by — the configured date, or the rolling default when none
    // is set. It's the baseline the newly saved date is compared against to detect a window change.
    const [effectiveStartDate, setEffectiveStartDate] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [validationError, setValidationError] = useState<string | null>(null);
    // Non-null ⇒ the resync prompt is open for that just-saved date.
    const [resyncPromptDate, setResyncPromptDate] = useState<string | null>(null);
    const [resyncOutcome, setResyncOutcome] = useState<SyncStartDateResyncOutcome>(null);
    const fetchStartDate = useSyncStartDateStore(s => s.fetchStartDate);

    useEffect(() => {
        fetchStartDate()
            .then(({ startDate, effectiveStartDate: effective }) => {
                setCurrentStartDate(startDate);
                setEffectiveStartDate(effective);
                setSelectedDate(startDate ?? '');
                setStatus('idle');
            })
            .catch(() => setStatus('load-error'));
    }, [fetchStartDate]);

    const handleSave = async () => {
        setValidationError(null);
        if (!selectedDate) return;

        const earliest = getEarliestAllowedDate();
        if (selectedDate < earliest) {
            setValidationError(t(TranslationKey.SyncStartDate.ErrorTooOld, { earliest }));
            setStatus('validation-error');
            return;
        }

        setStatus('saving');
        try {
            await syncService.setSyncStartDate(selectedDate);
            // Compare against the window that was in force (the configured date, else the rolling
            // default): re-saving the same effective date changes nothing, so it must not prompt.
            const windowChanged = selectedDate !== effectiveStartDate;
            setCurrentStartDate(selectedDate);
            setEffectiveStartDate(selectedDate);
            setStatus('saved');
            // Only offer the sync when one can actually run right now (bridge reachable + connected).
            // When it can't, the full sync is still required, so say so rather than staying silent.
            setResyncOutcome(windowChanged && !canSync ? 'unavailable' : null);
            if (windowChanged && canSync) setResyncPromptDate(selectedDate);
        } catch {
            setStatus('save-error');
        }
    };

    const handleResyncConfirm = () => {
        setResyncPromptDate(null);
        syncOnHome();
    };

    const handleResyncCancel = () => {
        setResyncPromptDate(null);
        setResyncOutcome('declined');
    };

    const isSaving = status === 'saving';

    return (
        <div data-testid="sync-start-date-panel">
            <h2 data-testid="sync-start-date-title" className="text-block-title text-content">{t(TranslationKey.SyncStartDate.Title)}</h2>

            {status === 'loading' ? (
                <p data-testid="sync-start-date-loading" className="text-primary-700 text-s mt-xs">{t(TranslationKey.Common.Loading)}</p>
            ) : status === 'load-error' ? (
                <p data-testid="sync-start-date-load-error" className="text-danger text-s mt-xs">{t(TranslationKey.SyncStartDate.ErrorLoadFailed)}</p>
            ) : (
                <>
                    <p data-testid="sync-start-date-description" className="text-primary-700 text-s mt-xs">
                        {currentStartDate
                            ? t(TranslationKey.SyncStartDate.Description)
                            : t(TranslationKey.SyncStartDate.DefaultDescription)}
                    </p>

                    <div className="mt-m flex flex-col gap-s">
                        <div className="flex items-end gap-s">
                            <DatePicker
                                id="sync-start-date-input"
                                data-testid="sync-start-date-input"
                                label={t(TranslationKey.SyncStartDate.DateLabel)}
                                value={selectedDate}
                                min={getEarliestAllowedDate()}
                                max={getTodayDate()}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    setValidationError(null);
                                    setResyncOutcome(null);
                                    if (status === 'saved' || status === 'save-error' || status === 'validation-error') {
                                        setStatus('idle');
                                    }
                                }}
                                disabled={isSaving}
                                className="w-48"
                            />
                            <Button
                                data-testid="sync-start-date-save-btn"
                                onClick={() => void handleSave()}
                                disabled={isSaving || syncing || !selectedDate}
                            >
                                {isSaving ? t(TranslationKey.SyncStartDate.Saving) : t(TranslationKey.SyncStartDate.SaveBtn)}
                            </Button>
                        </div>

                        <SyncStartDateFeedback
                            status={status}
                            validationError={validationError}
                            resyncOutcome={resyncOutcome}
                        />
                    </div>
                </>
            )}

            {resyncPromptDate && (
                <SyncStartDateResyncPrompt
                    startDate={resyncPromptDate}
                    onConfirm={handleResyncConfirm}
                    onCancel={handleResyncCancel}
                />
            )}
        </div>
    );
};
